import { defineStore, getActivePinia } from 'pinia'
import { getInfo } from '~/apis/auth'
import type { GetInfoResponse, AuthUser, AuthSession } from '~/types/auth'
import { usePlayerStore } from '~/stores/playerStore'
import { useMusicPreviewStore } from '~/stores/musicPreviewStore'

let storageListenerBound = false

// 获取持久令牌
const getPersistedToken = () => {
  try {
    const tokenCookie = useCookie<string | null>('token')
    const cookieToken = tokenCookie.value
    if (process.client) {
      return localStorage.getItem('token') || cookieToken || null
    }
    return cookieToken || null
  } catch {
    // 如果 useCookie 失败，尝试从 document.cookie 读取（仅在客户端）
    if (process.client && typeof document !== 'undefined') {
      const cookies = document.cookie.split(';')
      for (const cookie of cookies) {
        const [name, value] = cookie.trim().split('=')
        if (name === 'token') {
          const token = value || null
          return localStorage.getItem('token') || token || null
        }
      }
      return localStorage.getItem('token')
    }
    return null
  }
}

const getPersistedUser = () => {
  if (!process.client) return null
  try {
    const stored = localStorage.getItem('auth_user')
    return stored ? (JSON.parse(stored) as AuthUser) : null
  } catch {
    return null
  }
}

export const useAuthStore = defineStore('authStore', () => {
  const token = ref<string | null>(null)
  const user = ref<AuthUser | null>(null)
  const profileLoading = ref(false)
  const initialized = ref(false)
  const clientInitialized = ref(false) // 客户端初始化标记
  const clientAuthStarted = ref(false)
  const router = useRouter()
  const pinia = getActivePinia()

  const normalizeAvatarUrl = (value: unknown) => {
    const raw = typeof value === 'string' ? value : ''
    const trimmed = raw.replace(/[\u200B-\u200D\uFEFF]/g, '').trim()
    if (!trimmed) return ''

    if (trimmed.startsWith('//')) return `https:${trimmed}`
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed
    if (trimmed.startsWith('data:') || trimmed.startsWith('blob:')) return trimmed
    if (trimmed.startsWith('/') || trimmed.startsWith('./')) return trimmed

    // If backend returns a bare domain/path without scheme, treat it as https.
    if (/^[a-z0-9.-]+\.[a-z]{2,}(\/|\?|#|$)/i.test(trimmed)) return `https://${trimmed}`

    return trimmed
  }

  const hydrateFromStorage = () => {
    // 如果已初始化且有 token，不需要重新读取
    if (initialized.value && token.value) return
    const persistedToken = getPersistedToken()
    if (persistedToken) {
      token.value = persistedToken
    }
    initialized.value = true
  }

  // 同步 token 状态（用于路由切换时重新检查）
  const syncTokenFromStorage = () => {
    if (!process.client) return
    const storedToken = getPersistedToken()

    // 如果没有 token，确保清空用户状态，避免使用旧缓存导致 UI 误判为已登录
    if (!storedToken) {
      if (token.value !== null) token.value = null
      user.value = null
      return
    }

    // 如果缓存里有 user，优先用缓存填充（避免刷新时头像短暂回落到默认图）
    if (!user.value) {
      const cachedUser = getPersistedUser()
      if (cachedUser) user.value = cachedUser
    }

    // 只要存储里有 token，就确保 store 的 token 与之同步（避免某些情况下 token 已有但不一致）
    if (token.value !== storedToken) {
      token.value = storedToken
    }
  }

  const isAuthenticated = computed(() => Boolean(token.value))
  const credits = computed(() => {
    const rawCredits = user.value?.credits ?? user.value?.coin ?? 0
    const value = Number(rawCredits)
    return Number.isNaN(value) ? 0 : value
  })
  const displayName = computed(() => user.value?.nickname || user.value?.username || user.value?.email || 'User')
  const avatarUrl = computed(() => {
    return normalizeAvatarUrl(user.value?.avatar) || ''
  })

  const persistToken = (value: string | null) => {
    // 尝试使用 useCookie，如果失败（不在正确的上下文中），则只操作 localStorage
    try {
      const tokenCookie = useCookie<string | null>('token', {
        sameSite: 'lax',
        secure: process.env.PROD,
        httpOnly: false, // 需要客户端访问
        maxAge: 60 * 60 * 24 * 7 // 7 天过期
      })
      tokenCookie.value = value
    } catch (error) {
      // 如果 useCookie 失败（不在正确的 Nuxt 上下文中），在客户端直接操作 cookie
      if (process.client && typeof document !== 'undefined') {
        if (value) {
          const maxAge = 60 * 60 * 24 * 7 // 7 天
          const secure = process.env.PROD ? '; Secure' : ''
          document.cookie = `token=${value}; SameSite=Lax; Max-Age=${maxAge}${secure}; Path=/`
        } else {
          // 清除 cookie
          document.cookie = 'token=; SameSite=Lax; Max-Age=0; Path=/'
        }
      }
    }
    if (process.client) {
      try {
        if (value) localStorage.setItem('token', value)
        else localStorage.removeItem('token')
      } catch {
        // ignore localStorage errors (e.g., iOS private mode / restricted storage)
      }
    }
  }

  const persistUser = (value: AuthUser | null) => {
    if (!process.client) return
    try {
      if (value) localStorage.setItem('auth_user', JSON.stringify(value))
      else localStorage.removeItem('auth_user')
    } catch {
      // ignore localStorage errors (e.g., iOS private mode / restricted storage)
    }
  }

  const setSession = async (session: AuthSession) => {
    token.value = session.token
    persistToken(session.token)
    user.value = session.user || null
    persistUser(user.value)
    // 如果 session.user 不完整（如无 subscribe 等扩展字段），主动拉取完整资料
    // 异步获取用户信息，确保登录后能获取完整用户数据
    if (!session.user || !session.user.subscribe) {
      try {
        await fetchUserProfile()
      } catch (error) {
        console.error('[AuthStore] Failed to fetch user profile after login:', error)
      }
    }
  }

  const fetchUserProfile = async (force = false) => {
    if (!process.client) return
    if (!token.value) {
      console.warn('[AuthStore] fetchUserProfile: No token available')
      return
    }
    if (profileLoading.value && !force) {
      console.warn('[AuthStore] fetchUserProfile: Already loading, skipping')
      return
    }
    profileLoading.value = true
    try {
      const response = await getInfo({ loading: false, showSuccess: false })
      console.log('个人信息', response)
      if (response.success && response.data) {
        const data = response.data as GetInfoResponse
        const previousAvatar = user.value?.avatar
        const nextAvatarRaw = (data as any)?.avatar
        const nextAvatar = normalizeAvatarUrl(nextAvatarRaw)
        const mergedAvatar = nextAvatar || normalizeAvatarUrl(previousAvatar) || ''

        user.value = {
          ...(user.value || {}),
          ...data,
          avatar: mergedAvatar,
          credits: Number(data.coin ?? 0) || 0
        } as AuthUser
        persistUser(user.value)
      } else {
        // 如果获取用户信息失败，可能是 token 无效，清除 token
        console.warn('[AuthStore] fetchUserProfile: Failed to get user info, response:', response)
        logout()
      }
    } catch (error: any) {
      console.error('[AuthStore] fetchUserProfile: Error:', error)
      // 区分错误类型
      const isAuthError = error?.code === 401
        || error?.status === 401
        || error?.statusCode === 401
        || (error?.response?.status === 401)

      const isNetworkError = !error?.code
        && !error?.status
        && !error?.statusCode
        && (error?.message?.toLowerCase().includes('network')
          || error?.message?.toLowerCase().includes('timeout')
          || error?.message?.toLowerCase().includes('failed to fetch')
          || error?.name === 'NetworkError'
          || error?.name === 'TypeError')

      const isServerError = error?.code === 500
        || error?.status === 500
        || error?.statusCode === 500
        || (error?.response?.status === 500)

      if (isAuthError) {
        logout()
      } else if (isNetworkError) {
        // 网络错误：保留 token，不登出用户
      } else if (isServerError) {
        // 服务器错误：保留 token，不登出用户
      } else {
        // 其他错误：根据具体情况处理
      }
    } finally {
      profileLoading.value = false
    }
  }

  const logout = () => {
    const piniaInstance = pinia || getActivePinia()
    const playerStore = piniaInstance ? usePlayerStore(piniaInstance) : null
    const musicPreviewStore = piniaInstance ? useMusicPreviewStore(piniaInstance) : null

    // 重置播放器和预览区
    playerStore?.cleanup()
    musicPreviewStore?.clearPreviewSong()

    // 清除 token 状态
    token.value = null
    user.value = null
    persistUser(null)
    // 清除持久化存储中的 token（localStorage 和 cookie）
    persistToken(null)
    // 双重保险：确保 localStorage 被清除（防止某些边缘情况）
    if (process.client) {
      try {
        localStorage.removeItem('token')
      } catch {
        // ignore
      }
      // 退出后返回首页
      router.push('/')
    }
  }

  const startClientAuth = () => {
    if (!process.client) return
    if (clientAuthStarted.value) return
    clientAuthStarted.value = true

    syncTokenFromStorage()

    if (!storageListenerBound) {
      window.addEventListener('storage', (event) => {
        if (event.key === 'token') {
          token.value = event.newValue
          user.value = null
        }
      })
      storageListenerBound = true
    }

    watch(() => token.value, (current, previous) => {
      if (current === previous) return
      if (!current) {
        user.value = null
        return
      }
      if ((!user.value || !user.value.avatar) && !profileLoading.value) {
        fetchUserProfile(true).catch(() => {})
      }
    })

    if (token.value && (!user.value || !user.value.avatar) && !profileLoading.value) {
      fetchUserProfile(true).catch(() => {})
    }
  }

  const initialize = async () => {
    startClientAuth()
  }

  // SSR：只读取 cookie token，不拉取用户信息（避免 SSR 期间请求 getInfo）
  if (process.server) {
    hydrateFromStorage()
  }

  return {
    token,
    user,
    profileLoading,
    isAuthenticated,
    credits,
    displayName,
    avatarUrl,
    setSession,
    fetchUserProfile,
    logout,
    startClientAuth,
    initialize,
    syncTokenFromStorage
  }
})
